import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { Trash2, Loader, Eye, X, Shield, Settings, Save, ChevronDown, ChevronUp } from 'lucide-react';
import useSWR from 'swr';

import NewCompanyService from '../../services/company-service';
import { STATUS_MESSAGES } from '../../utils/api-handlers/http-status-code';
import { showToast } from '../../components/ToastConfig';
import { useAuth } from '../../contexts/AuthContext';

const PAGE_SIZE = 10;

const fetcher = async ([url, keyword, page, filters]) => {
  try {
    const res = await NewCompanyService.search(keyword, page, PAGE_SIZE, filters);
    return res.data;
  } catch (error) {
    if (error.response?.status === 400 && ["1002", "8000"].includes(error.response?.data?.errorCode)) {
      return { data: [], pagination: { totalCount: 0 } };
    }
    throw error;
  }
};

const StatusBadge = ({ status }) => {
  const colors = {
    Active: "bg-green-100 text-green-800 border-green-200",
    Pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
    Inactive: "bg-red-100 text-red-800 border-red-200",
  };
  if (!status) return <span className="text-xs text-gray-300">-</span>;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${colors[status] || "bg-gray-100 text-gray-800 border-gray-200"}`}>
      {status}
    </span>
  );
};

function CompanyList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('keyword') || '');
  const [pageInput, setPageInput] = useState(searchParams.get('page') || '1');
  const [isComposing, setIsComposing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [notificationEmails, setNotificationEmails] = useState('');
  const [settingsLoading, setSettingsLoading] = useState(false);
  const Navigate = useNavigate();
  const { logout } = useAuth();

  // 載入設定
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await NewCompanyService.getSettings();
        setNotificationEmails(res.data?.CompanyReviewNotificationEmails || '');
      } catch {}
    };
    loadSettings();
  }, []);

  const saveSettings = async () => {
    setSettingsLoading(true);
    try {
      await NewCompanyService.updateSettings({ companyReviewNotificationEmails: notificationEmails });
      showToast.success('Settings saved');
    } catch (err) {
      showToast.error('Failed to save settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const keyword = searchParams.get('keyword') || '';
  const page = parseInt(searchParams.get('page') || '1');
  const statusFilter = searchParams.get('status') || '';
  const regionInput = searchParams.get('region') || '';
  const pendingMembersFilter = searchParams.get('pendingMembers') === 'true';

  const hasFilters = !!(statusFilter || regionInput || pendingMembersFilter);

  // 組合後端篩選參數
  const filters = {
    reviewStatus: statusFilter || null,
    hasPendingMembers: pendingMembersFilter || null,
    region: regionInput || null,
  };

  const { data, error, isLoading, mutate } = useSWR(
    ['/api/CompanyManagement/Search', keyword, page, filters],
    ([url, kw, pg, f]) => fetcher([url, kw, pg, f]),
    {
      onError: (err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) logout();
        else if (err.response?.status !== 400) {
          showToast.error(STATUS_MESSAGES[err.response?.status] || STATUS_MESSAGES["default"]);
        }
      },
      revalidateOnFocus: false,
    }
  );

  const companies = data?.data || [];
  const totalItems = data?.pagination?.totalCount || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  const updateSearchParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) newParams.set(key, value);
      else newParams.delete(key);
    });
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const setFilter = useCallback((key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set(key, value);
    else newParams.delete(key);
    newParams.set('page', '1');
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const clearFilters = () => {
    const newParams = new URLSearchParams();
    if (keyword) newParams.set('keyword', keyword);
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  // 同步 pageInput
  useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  // 搜尋防抖
  useEffect(() => {
    if (isComposing || searchInput === keyword) return;
    const timer = setTimeout(() => updateSearchParams({ keyword: searchInput, page: '1' }), 500);
    return () => clearTimeout(timer);
  }, [searchInput, isComposing, updateSearchParams, keyword]);

  const handleDelete = async (e, companyId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Delete this company? This cannot be undone.')) return;
    try {
      await NewCompanyService.delete(companyId);
      mutate();
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) logout();
      else showToast.error('Failed to delete');
    }
  };

  return (
    <div className="p-6 pt-0">
      <div className="container mx-auto">
        {/* Header */}
        <div className="pt-6 pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">Manage Company</h1>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`p-1.5 rounded hover:bg-gray-100 transition-colors ${showSettings ? 'text-blue-600' : 'text-gray-400'}`}
                title="Notification Settings"
              >
                <Settings size={18} />
              </button>
            </div>
            <input
              type="text"
              placeholder="Search Company..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onCompositionStart={() => setIsComposing(true)}
              onCompositionEnd={() => setIsComposing(false)}
              className="px-3 py-1.5 border rounded text-sm w-64 focus:outline-none focus:border-gray-400"
            />
          </div>

          {/* Settings Panel */}
          {showSettings && (
            <div className="mt-3 p-4 bg-gray-50 rounded-lg border">
              <h3 className="text-sm font-medium mb-2">Notification Settings</h3>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="text-xs text-gray-500 mb-1 block">Review Notification Emails (comma separated)</label>
                  <input
                    type="text"
                    value={notificationEmails}
                    onChange={(e) => setNotificationEmails(e.target.value)}
                    placeholder="admin1@biodnd.com, admin2@biodnd.com"
                    className="w-full px-3 py-1.5 border rounded text-sm focus:outline-none focus:border-gray-400"
                  />
                </div>
                <button
                  onClick={saveSettings}
                  disabled={settingsLoading}
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                >
                  <Save size={14} /> {settingsLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap mt-3">
            <span className="text-xs text-gray-400 font-medium">Status:</span>
            {["Pending", "Active", "Inactive"].map((s) => {
              const isActive = statusFilter === s;
              const colors = {
                Pending: isActive ? "bg-yellow-500 text-white" : "bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-200",
                Active: isActive ? "bg-green-600 text-white" : "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200",
                Inactive: isActive ? "bg-red-600 text-white" : "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200",
              };
              return (
                <button key={s} onClick={() => setFilter('status', isActive ? '' : s)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${colors[s]}`}>
                  {s}
                </button>
              );
            })}

            <span className="text-gray-200 mx-1">|</span>
            <button
              onClick={() => setFilter('pendingMembers', pendingMembersFilter ? '' : 'true')}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${
                pendingMembersFilter
                  ? "bg-orange-500 text-white"
                  : "bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200"
              }`}
            >
              <Shield size={12} /> Pending Members
            </button>

            <span className="text-gray-200 mx-1">|</span>
            <span className="text-xs text-gray-400 font-medium">Region:</span>
            <input
              type="text"
              placeholder="e.g. US, TW"
              value={searchParams.get('region') || ''}
              onChange={(e) => setFilter('region', e.target.value.trim())}
              className="px-2 py-1 border rounded text-xs w-24 focus:outline-none focus:border-gray-400"
            />

            {hasFilters && (
              <button onClick={clearFilters} className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-red-500">
                <X size={12} /> Clear
              </button>
            )}

            <span className="ml-auto text-xs text-gray-400">
              {isLoading ? '' : `${totalItems} results`}
            </span>
          </div>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="text-center py-12 text-gray-500 flex items-center justify-center">
            <Loader className="mr-2 animate-spin w-5 h-5" /> Loading...
          </div>
        )}

        {/* Error */}
        {error && !isLoading && (
          <div className="text-center py-12 text-red-500">Failed to load data</div>
        )}

        {/* Empty */}
        {!isLoading && !error && companies.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            {keyword || hasFilters ? 'No companies match the criteria' : 'No companies found'}
          </div>
        )}

        {/* Table */}
        {!isLoading && !error && companies.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Region</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Create Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {companies.map(company => {
                  const displayName = company.organization || company.name || '(unnamed)';
                  const reviewStatus = company.review_status;
                  const isPending = reviewStatus === 'Pending';

                  return (
                    <tr key={company.uuid} className={`hover:bg-gray-50 transition-colors ${isPending ? 'bg-yellow-50/30' : ''}`}>
                      <td className="px-4 py-3">
                        <Link to={`/company/profile/${company.uuid}`} className="group">
                          <div className="text-sm font-medium text-gray-900 group-hover:text-blue-600">{displayName}</div>
                          <div className="text-xs text-gray-400 font-mono mt-0.5">{company.uuid?.substring(0, 24)}...</div>
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{company.region || company.country_code || '-'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <StatusBadge status={reviewStatus} />
                          {company.pending_members_count > 0 && (
                            <span className="px-1.5 py-0.5 bg-orange-500 text-white text-xs rounded-full leading-none flex items-center gap-0.5" title={`${company.pending_members_count} pending member(s)`}>
                              <Shield size={10} /> {company.pending_members_count}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {company.created_at || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Link to={`/company/profile/${company.uuid}`}
                            className="px-2.5 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 flex items-center gap-1">
                            <Eye size={14} /> View
                          </Link>
                          <button onClick={(e) => handleDelete(e, company.uuid)}
                            className="text-gray-300 hover:text-red-500 transition-colors" title="Delete">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="flex items-center justify-center gap-2 mt-6 pb-6">
            <button onClick={() => updateSearchParams({ page: String(page - 1) })}
              disabled={page === 1}
              className={`px-3 py-1.5 border rounded text-sm ${page === 1 ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100'}`}>
              Prev
            </button>

            {totalPages > 1 && Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pageNum;
              if (totalPages <= 7) pageNum = i + 1;
              else if (page <= 4) pageNum = i + 1;
              else if (page >= totalPages - 3) pageNum = totalPages - 6 + i;
              else pageNum = page - 3 + i;
              return (
                <button key={pageNum} onClick={() => updateSearchParams({ page: String(pageNum) })}
                  className={`px-3 py-1.5 border rounded text-sm min-w-[36px] ${pageNum === page ? 'bg-black text-white' : 'hover:bg-gray-100'}`}>
                  {pageNum}
                </button>
              );
            })}

            <button onClick={() => updateSearchParams({ page: String(page + 1) })}
              disabled={page === totalPages}
              className={`px-3 py-1.5 border rounded text-sm ${page === totalPages ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100'}`}>
              Next
            </button>

            <span className="text-gray-300 mx-1">|</span>

            <span className="text-xs text-gray-500">Page</span>
            <input
              type="text"
              value={pageInput}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                setPageInput(val);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const num = parseInt(pageInput);
                  if (!isNaN(num) && num >= 1 && num <= totalPages) {
                    updateSearchParams({ page: String(num) });
                  } else {
                    setPageInput(String(page));
                  }
                }
              }}
              onBlur={() => {
                const num = parseInt(pageInput);
                if (isNaN(num) || num < 1 || num > totalPages) {
                  setPageInput(String(page));
                }
              }}
              className="w-14 px-2 py-1 border rounded text-sm text-center focus:outline-none focus:border-gray-400"
            />
            <span className="text-xs text-gray-500">/ {totalPages}</span>
            <span className="text-xs text-gray-400 ml-2">({totalItems} total)</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default CompanyList;
